import {
  canWith, interactionsSummary, pickFromPrincipals, principalsFromPick, INTERACTIONS_ALWAYS_NOTE, INTERACTIONS_LABEL, REACT_OPTIONS, REPLY_OPTIONS,
  type AnnouncementInteractions, type AudienceMatch, type FeatureFileUrl, type InteractionPolicy,
} from '@tradeoutlet/shared';
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Building2, ChevronDown, MessageSquare, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { useLocations, usePositions, useTeams, useWorkspaceId } from '@/lib/hooks';
import { useCan, usePermissions } from '@/lib/permissions';
import { useDebounced } from '@/lib/useDebounced';
import { AudiencePicker as ChipPicker, type AudienceKind } from '@/features/people/AudiencePicker';
import type { ResolveFile } from './editor';

export const kKeys = (ws: string, ...rest: unknown[]) => ['knowledge', ws, ...rest] as const;

/** Buildings and teams; none ticked = everyone. Options outside the caller's grant are disabled. */
export function AudiencePicker({ value, onChange, perm }: { value: { location_ids: string[]; team_ids: string[] }; onChange: (v: { location_ids: string[]; team_ids: string[] }) => void; perm?: string }) {
  const locations = useLocations().data?.locations ?? [];
  const { teams } = useTeams();
  const wide = useCan(perm ?? '__none__');
  const toggle = (k: 'location_ids' | 'team_ids', id: string) =>
    onChange({ ...value, [k]: value[k].includes(id) ? value[k].filter(x => x !== id) : [...value[k], id] });
  const everyone = !value.location_ids.length && !value.team_ids.length;
  return (
    <div className="space-y-2 text-sm">
      <label className={`flex items-center gap-2 ${perm && !wide ? 'opacity-50' : ''}`}>
        <input type="radio" checked={everyone} disabled={Boolean(perm && !wide)} onChange={() => onChange({ location_ids: [], team_ids: [] })} />Everyone in the company
      </label>
      <div className="grid gap-2 sm:grid-cols-2">
        <div><div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500"><Building2 className="h-3 w-3" />Buildings</div>
          {locations.filter(l => !l.archived_at).map(l => <ScopedCheck key={l.id} perm={perm} refs={[`location:${l.id}`]} checked={value.location_ids.includes(l.id)} onChange={() => toggle('location_ids', l.id)} label={l.name} />)}</div>
        <div><div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500"><Users className="h-3 w-3" />Departments &amp; groups</div>
          {teams.map(t => <ScopedCheck key={t.id} perm={perm} refs={[`team:${t.id}`]} checked={value.team_ids.includes(t.id)} onChange={() => toggle('team_ids', t.id)} label={t.name} />)}</div>
      </div>
    </div>
  );
}
export interface AnnouncementAudienceValue { location_ids: string[]; team_ids: string[]; position_ids?: string[]; audience_match: AudienceMatch }

/**
 * t140: announcement "Who sees it". Buildings, departments and groups searched together with chips (so long
 * lists stay manageable), only what you can post to is offered, a live reach count, and with buildings and teams both
 * picked, "Only people in both" narrows it to people in one of the buildings who are also in one of the teams.
 */
export function AnnouncementAudience({ value, onChange, perm = 'comms.announcement.publish' }: { value: AnnouncementAudienceValue; onChange: (v: AnnouncementAudienceValue) => void; perm?: string }) {
  const ws = useWorkspaceId();
  const mine = usePermissions().data;
  const wide = useCan(perm);
  const positions = value.position_ids ?? [];
  const hasBuildings = value.location_ids.length > 0;
  // t521u: a position spans every building, so without a company-wide grant it can only be narrowed to your buildings.
  const allow = useCallback((k: AudienceKind, id: string) => k === 'position' ? wide || hasBuildings
    : canWith(mine, perm, [k === 'building' ? `location:${id}` : `team:${id}`]), [mine, perm, wide, hasBuildings]);
  const both = hasBuildings && (value.team_ids.length > 0 || positions.length > 0);
  const match: AudienceMatch = both ? value.audience_match : 'any';
  const everyone = !hasBuildings && !value.team_ids.length && !positions.length;
  const key = useDebounced(JSON.stringify({ l: [...value.location_ids].sort(), t: [...value.team_ids].sort(), p: [...positions].sort(), m: match }), 250);
  const reach = useQuery({
    queryKey: ['announcement-reach', ws, key],
    queryFn: () => { const k = JSON.parse(key) as { l: string[]; t: string[]; p: string[]; m: AudienceMatch }; return api.announcements.reach(ws!, { location_ids: k.l, team_ids: k.t, position_ids: k.p, audience_match: k.m }); },
    enabled: Boolean(ws) && (wide || !everyone), staleTime: 30_000, placeholderData: prev => prev,
  });
  const seg = (on: boolean) => clsx('rounded-md px-2.5 py-1 text-xs', on ? 'bg-slate-900 font-medium text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white');
  return (
    <div className="space-y-2 text-sm" data-testid="announcement-audience">
      <ChipPicker kinds={['building', 'department', 'group', 'position']} allow={allow} placeholder="Search buildings, departments, groups and positions" label="Add buildings, departments, groups or positions"
        value={{ people: [], teams: value.team_ids, buildings: value.location_ids, positions }}
        onChange={v => {
          const next = { ...value, location_ids: v.buildings ?? [], team_ids: v.teams, position_ids: v.positions ?? [] };
          // Building-scoped publishers: a newly added position narrows to their buildings.
          if (!wide && next.position_ids.length && !positions.length) next.audience_match = 'both';
          onChange(next);
        }} />
      {both && (
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800" role="radiogroup" aria-label="Who in these" data-testid="audience-match">
          <button type="button" role="radio" aria-checked={match === 'any'} className={seg(match === 'any')} onClick={() => onChange({ ...value, audience_match: 'any' })}>In any of these</button>
          <button type="button" role="radio" aria-checked={match === 'both'} className={seg(match === 'both')} onClick={() => onChange({ ...value, audience_match: 'both' })}
            title="People in one of the buildings who are also in one of the departments, groups or positions">Only people in both</button>
        </div>
      )}
      {!wide && positions.length > 0 && match !== 'both' && (
        <p className="text-xs text-slate-500" data-testid="audience-position-hint">Positions cover every building, so add your buildings and choose Only people in both.</p>
      )}
      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
        <span>{everyone ? (wide ? 'Everyone in the company' : 'Pick the buildings or teams to post to') : null}</span>
        {(wide || !everyone) && reach.data && <span className="shrink-0 font-medium text-slate-700 dark:text-slate-200" data-testid="audience-reach">Reaches {reach.data.count} {reach.data.count === 1 ? 'person' : 'people'}</span>}
      </div>
    </div>
  );
}

/**
 * t522u: the composer's collapsed "Replies and reactions" row. Closed it reads as one muted line ("Everyone can reply and
 * react"); open it offers who can reply and who can react, and "Only selected people" reuses the audience search (people,
 * departments, groups, buildings, positions). The author, owners and admins always can.
 */
export function InteractionsField({ value, onChange }: { value: AnnouncementInteractions; onChange: (v: AnnouncementInteractions) => void }) {
  const reply = value.reply_policy ?? 'everyone', react = value.react_policy ?? 'everyone';
  const [open, setOpen] = useState(reply !== 'everyone' || react !== 'everyone');
  const seg = (on: boolean) => clsx('rounded-md px-2.5 py-1 text-xs', on ? 'bg-slate-900 font-medium text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white');
  const group = (what: 'reply' | 'react', label: string, opts: { key: InteractionPolicy; label: string }[], cur: InteractionPolicy) => (
    <div className="space-y-1.5" data-testid={`interactions-${what}`}>
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</div>
      <div className="inline-flex flex-wrap rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800" role="radiogroup" aria-label={label}>
        {opts.map(o => <button key={o.key} type="button" role="radio" aria-checked={cur === o.key} className={seg(cur === o.key)}
          onClick={() => onChange({ ...value, [`${what}_policy`]: o.key })}>{o.label}</button>)}
      </div>
      {cur === 'selected' && (
        <ChipPicker kinds={['person', 'position', 'department', 'group', 'building']} placeholder="Search people, positions, departments, groups and buildings" label={`Add who can ${what}`}
          value={pickFromPrincipals(value[`${what}_principals`])} onChange={v => onChange({ ...value, [`${what}_principals`]: principalsFromPick(v) })} />
      )}
    </div>
  );
  return (
    <div className="rounded-lg border border-slate-200 text-sm dark:border-slate-800" data-testid="announcement-interactions">
      <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <MessageSquare className="h-4 w-4 shrink-0 text-slate-400" />
        <span className="font-medium text-slate-700 dark:text-slate-200">{INTERACTIONS_LABEL}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-slate-500" data-testid="interactions-summary">{interactionsSummary(reply, react)}</span>
        <ChevronDown className={clsx('h-4 w-4 shrink-0 text-slate-400 transition', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-200 px-3 py-3 dark:border-slate-800">
          {group('reply', 'Who can reply', REPLY_OPTIONS, reply)}
          {group('react', 'Who can react', REACT_OPTIONS, react)}
          <p className="text-xs text-slate-500">{INTERACTIONS_ALWAYS_NOTE}</p>
        </div>
      )}
    </div>
  );
}

function ScopedCheck({ perm, refs, checked, onChange, label }: { perm?: string; refs: string[]; checked: boolean; onChange: () => void; label: string }) {
  const ok = useCan(perm ?? 'chat.message.post', perm ? refs : []);
  const allowed = !perm || ok;
  return <label className={`flex items-center gap-2 py-0.5 ${allowed ? '' : 'opacity-50'}`} title={allowed ? undefined : 'Outside your permissions'}>
    <input type="checkbox" checked={checked} disabled={!allowed && !checked} onChange={onChange} />{label}</label>;
}

export function useAudienceLabel() {
  const locations = useLocations().data?.locations ?? [];
  const { byId } = useTeams();
  const positions = usePositions().data?.positions ?? [];
  return (a: { location_ids: string[]; team_ids: string[]; position_ids?: string[] }) => (!a.location_ids.length && !a.team_ids.length && !a.position_ids?.length ? 'Everyone'
    : [...a.location_ids.map(id => locations.find(l => l.id === id)?.name ?? 'Building'), ...a.team_ids.map(id => byId.get(id)?.name ?? 'Department'),
      ...(a.position_ids ?? []).map(id => positions.find(p => p.id === id)?.name ?? 'Position')].join(', '));
}

/**
 * t660u: the line under an article's "Who can read it", only when its collection is restricted (null otherwise).
 * `audience` is the collection's audience as a plain list of names ("Warehouse, Retail").
 */
export function collectionAudienceNote(collection: { name: string; restricted: boolean; audience: string } | null | undefined, articleRestricted: boolean): string | null {
  if (!collection?.restricted) return null;
  return `This article is in ${collection.name}, which only ${collection.audience} can see, so ${articleRestricted ? 'only people in both will see it' : 'only they will see it'}.`;
}

/** Resolver for uploads inside an announcement / article (falls back to the uploader's own read while drafting). */
export function useResolver(kind: 'announcement' | 'kb', id: string | null | undefined): ResolveFile {
  const ws = useWorkspaceId();
  return useCallback(async (att: string) => {
    if (!ws) return null;
    try {
      if (id) return (kind === 'kb' ? await api.kb.file(ws, id, att) : await api.announcements.file(ws, id, att)).url;
    } catch { /* not yet saved on this item: try the uploader's own access */ }
    try { return (await api.attachmentUrl(att, false)).url; } catch { return null; }
  }, [ws, kind, id]);
}

/** Feedback 1 #7: full file info (thumb_url + dimensions for images) through the owning feature's endpoint. */
export function useFeatureFile(kind: 'announcement' | 'kb', id: string | null | undefined) {
  const ws = useWorkspaceId();
  return useCallback(async (att: string): Promise<FeatureFileUrl | null> => {
    if (!ws || !id) return null;
    try { return kind === 'kb' ? await api.kb.file(ws, id, att) : await api.announcements.file(ws, id, att); } catch { return null; }
  }, [ws, kind, id]);
}

export const PRIORITY_STYLE: Record<string, string> = {
  urgent: 'bg-rose-600 text-white', important: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100', normal: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
};
